import { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";

export const useArticleManagement = (
  fetchArticlesFunction,
  updateArticleFunction,
  options = {}
) => {
  // Options par défaut
  const {
    enableModal = true,
    enableEdit = true,
    customNavigationHandler = null,
  } = options;

  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Modal (seulement si activé)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalSize, setModalSize] = useState("lg");
  const [modalContent, setModalContent] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Edit mode (seulement si activé)
  const [isEditMode, setIsEditMode] = useState(false);

  const fetchArticles = async () => {
    setIsLoading(true);
    try {
      const response = await fetchArticlesFunction();
      if (response?.data) {
        setArticles(response.data);
        setFilteredArticles(response.data);
        setTotalItems(response.data.length);
      }
    } catch (err) {
      console.error("Erreur lors de la récupération des articles:", err);
      setError(
        "Impossible de charger les articles. Veuillez réessayer plus tard."
      );
      setArticles([]);
      setFilteredArticles([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  };

  const filterArticles = useCallback(() => {
    if (searchTerm.trim() === "") {
      setFilteredArticles(articles);
      setTotalItems(articles.length);
      return;
    }

    const filtered = articles.filter((article) => {
      const searchLower = searchTerm.toLowerCase();
      const titleFr = article.title_art?.fr?.toLowerCase() || "";
      const titleEn = article.title_art?.en?.toLowerCase() || "";
      const resumeFr = article.resume_art?.fr?.toLowerCase() || "";
      const resumeEn = article.resume_art?.en?.toLowerCase() || "";
      const authorName = `${article.author?.first_name || ""} ${
        article.author?.last_name || ""
      }`.toLowerCase();

      return (
        titleFr.includes(searchLower) ||
        titleEn.includes(searchLower) ||
        resumeFr.includes(searchLower) ||
        resumeEn.includes(searchLower) ||
        authorName.includes(searchLower) ||
        article.study_area_art?.toLowerCase().includes(searchLower)
      );
    });

    setFilteredArticles(filtered);
    setTotalItems(filtered.length);
    setCurrentPage(1);
  }, [searchTerm, articles]);

  useEffect(() => {
    fetchArticles();
  }, []);

  useEffect(() => {
    filterArticles();
  }, [searchTerm, articles, filterArticles]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const getArticleTitle = (article) => {
    return article.title_art?.fr || article.title_art?.en || "Sans titre";
  };

  const getArticleAuthor = (article) => {
    if (article.author) {
      return `${article.author.title_aut || ""} ${
        article.author.first_name || ""
      } ${article.author.last_name || ""}`.trim();
    }
    return "Auteur inconnu";
  };

  const getArticleDate = (article) => {
    return article.updated_at || null;
  };

  // Fonction de gestion des détails flexible
  const handleViewDetails = useCallback(
    (article) => {
      // Si un gestionnaire personnalisé est fourni, l'utiliser
      if (customNavigationHandler) {
        customNavigationHandler(article);
        return;
      }

      // Sinon, utiliser le comportement par défaut (modal) si activé
      if (enableModal) {
        setSelectedArticle(article);
        setIsEditMode(false);
        setModalTitle(`Détails de l'article: ${getArticleTitle(article)}`);
        setModalSize("lg");

        setModalContent(
          <div className="article-details">
            <p className="text-gray-600">
              Gestionnaire de navigation personnalisé requis pour afficher les
              détails.
            </p>
          </div>
        );

        setIsModalOpen(true);
      }
    },
    [customNavigationHandler, enableModal]
  );

  // Edition (seulement si activée)
  const handleEditArticle = useCallback(
    (article) => {
      if (!enableEdit) return;

      setSelectedArticle(article);
      setIsEditMode(true);
      setModalTitle(`Modifier l'article: ${getArticleTitle(article)}`);
      setModalSize("xl");

      setModalContent(
        <div className="article-edit-form">
          <p className="text-gray-600">
            Composant d'édition à implémenter par le parent.
          </p>
        </div>
      );

      setIsModalOpen(true);
    },
    [enableEdit]
  );

  const handleUpdateArticle = useCallback(
    async (articleId, updatedData) => {
      if (!enableEdit || !updateArticleFunction) return;

      setIsSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);

      try {
        Swal.fire({
          title: "Mise à jour en cours...",
          html: `
          <div class="flex flex-col items-center">
            <div class="relative w-16 h-16">
              <div class="absolute top-0 left-0 w-16 h-16 border-4 border-[#57A863] border-opacity-20 rounded-full"></div>
              <div class="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-[#57A863] rounded-full animate-spin"></div>
            </div>
            <p class="mt-4 text-gray-700">Veuillez patienter pendant la mise à jour...</p>
          </div>
        `,
          showConfirmButton: false,
          allowOutsideClick: false,
        });

        const formData = new FormData();
        formData.append("_method", "PUT");

        Object.entries(updatedData).forEach(([key, value]) => {
          if (
            typeof value === "object" &&
            value !== null &&
            !(value instanceof File)
          ) {
            formData.append(key, JSON.stringify(value));
          } else if (value !== undefined) {
            formData.append(key, value);
          }
        });

        if (updatedData.file_art instanceof File) {
          formData.append("file_art", updatedData.file_art);
        } else {
          formData.append("file_art", "");
        }

        const response = await updateArticleFunction(articleId, formData);

        Swal.close();

        if (response && response.success) {
          const updatedArticles = articles.map((article) =>
            article.id_art === articleId ? response.data : article
          );

          setArticles(updatedArticles);
          setSubmitSuccess(true);

          await Swal.fire({
            icon: "success",
            title: "Succès !",
            text: "Article mis à jour avec succès.",
            confirmButtonColor: "#57A863",
          });

          setIsModalOpen(false);
          setIsEditMode(false);
          setSelectedArticle(null);
        } else {
          throw new Error(
            response?.message || "Échec de la mise à jour de l'article"
          );
        }
      } catch (err) {
        console.error("Erreur lors de la mise à jour de l'article:", err);

        Swal.fire({
          icon: "error",
          title: "Erreur",
          text:
            err.message ||
            "Une erreur s'est produite lors de la mise à jour de l'article.",
          confirmButtonColor: "#d33",
        });

        setSubmitError(
          err.message || "Échec de la mise à jour. Veuillez réessayer."
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [articles, updateArticleFunction, enableEdit]
  );

  // Pagination des données
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredArticles.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return {
    articles,
    filteredArticles,
    searchTerm,
    isLoading,
    error,
    currentPage,
    itemsPerPage,
    totalItems,
    isModalOpen: enableModal ? isModalOpen : false,
    modalTitle,
    modalSize,
    modalContent,
    selectedArticle,
    isSubmitting,
    submitError,
    submitSuccess,
    isEditMode: enableEdit ? isEditMode : false,
    fetchArticles,
    handlePageChange,
    handleItemsPerPageChange,
    handleSearch,
    getArticleTitle,
    getArticleAuthor,
    getArticleDate,
    handleEditArticle: enableEdit ? handleEditArticle : () => {},
    handleUpdateArticle: enableEdit ? handleUpdateArticle : () => {},
    handleViewDetails,
    setIsModalOpen: enableModal ? setIsModalOpen : () => {},
    setModalTitle,
    setModalSize,
    setModalContent,
    setSelectedArticle,
    setIsSubmitting,
    setSubmitError,
    setSubmitSuccess,
    setIsEditMode: enableEdit ? setIsEditMode : () => {},
    currentItems,
    indexOfFirstItem,
    indexOfLastItem,
    totalPages,
  };
};
